import React, { useEffect, useMemo, useState } from 'react';

import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';

import FilledButton from '../../components/Buttons/FilledButton';
import OutlinedButton from '../../components/Buttons/OutlinedButton';
import closeIcon from '../../assets/images/closeIcon.svg';

import classes from './styles.module.scss';

const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 12,
    borderColor: 'var(--text)',
    minHeight: 56,
    boxShadow: 'none',
    fontSize: '20px',
    lineHeight: '28px',
    paddingLeft: '4px',
    ':hover': {
      borderColor: 'var(--primary)',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'rgba(122, 178, 255, 0.18)' : undefined,
    color: '#1b1c1f',
    fontSize: '20px',
    lineHeight: '28px',
    padding: '12px 16px',
    cursor: 'pointer',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 12,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    marginTop: '4px',
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    padding: '4px',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgba(122, 178, 255, 0.24)',
    borderRadius: '8px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#1b1c1f',
    fontSize: '18px',
    lineHeight: '26px',
    padding: '4px 8px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    ':hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      color: '#ef4444',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#666666',
    fontSize: '20px',
  }),
};

const USERS_SELECT_ID = 'training-path-assign-users';
const PATHS_SELECT_ID = 'training-path-assign-paths';
const USERS_LABEL_ID = 'training-path-assign-users-label';
const PATHS_LABEL_ID = 'training-path-assign-paths-label';

export default function TrainingPathAssignModal({
  isOpen,
  onClose,
  users,
  trainingPathOptions,
  onAssign,
  preSelectedTrainingPath,
  isSinglePathSelection = false,
}) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedUsers([]);
      setSelectedPath(null);
      setSelectedPaths([]);
    } else if (isOpen && preSelectedTrainingPath && isSinglePathSelection) {
      // Pre-fill the training path when modal opens
      const pathOption = trainingPathOptions.find(
        (opt) => opt.value === preSelectedTrainingPath
      );
      if (pathOption) {
        setSelectedPath(pathOption);
      }
    }
  }, [isOpen, preSelectedTrainingPath, isSinglePathSelection, trainingPathOptions]);

  const userOptions = useMemo(() => {
    return users.map((user) => ({
      value: user.id,
      label: `${user.name || user.email} · ${user.title || 'No title'}`,
    }));
  }, [users]);

  const handleSubmit = async () => {
    if (isSinglePathSelection && (!selectedUsers.length || !selectedPath)) {
      return;
    }
    if (!isSinglePathSelection && (!selectedUsers.length || !selectedPaths.length)) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onAssign({
        userIds: selectedUsers.map((option) => option.value),
        trainingPathIds: isSinglePathSelection
          ? [selectedPath.value]
          : selectedPaths.map((option) => option.value),
      });
      setSelectedUsers([]);
      setSelectedPath(null);
      setSelectedPaths([]);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      centered
      onHide={onClose}
      contentClassName={classes.AssignModal}
      dialogClassName={classes.dialog}
      backdropClassName={classes.backdrop}
    >
      <button type="button" className={classes.closeButton} onClick={onClose}>
        <img src={closeIcon} alt="Close" />
      </button>

      <div className={classes.content}>
        <h3>Add Trainee</h3>
        <p>
          {isSinglePathSelection
            ? 'Select existing users to assign to this training path.'
            : 'Select existing users and assign one or more training paths.'}
        </p>

        <div className={classes.field}>
          <span id={USERS_LABEL_ID}>Users</span>
          <Select
            isMulti
            value={selectedUsers}
            onChange={setSelectedUsers}
            options={userOptions}
            styles={selectStyles}
            classNamePrefix="assign-select"
            placeholder="Search users"
            inputId={USERS_SELECT_ID}
            aria-labelledby={USERS_LABEL_ID}
          />
        </div>

        <div className={classes.field}>
          <span id={PATHS_LABEL_ID}>Training paths</span>
          <Select
            isMulti={!isSinglePathSelection}
            value={isSinglePathSelection ? selectedPath : selectedPaths}
            onChange={(option) => {
              if (isSinglePathSelection) {
                setSelectedPath(option);
              } else {
                setSelectedPaths(option || []);
              }
            }}
            options={trainingPathOptions}
            styles={selectStyles}
            classNamePrefix="assign-select"
            placeholder="Search training paths"
            inputId={PATHS_SELECT_ID}
            aria-labelledby={PATHS_LABEL_ID}
            isDisabled={isSinglePathSelection && Boolean(preSelectedTrainingPath)}
          />
        </div>

        <div className={classes.footer}>
          <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
          <FilledButton
            isDisabled={
              !selectedUsers.length ||
              (isSinglePathSelection ? !selectedPath : !selectedPaths.length) ||
              isSubmitting
            }
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Assigning...' : 'Assign'}
          </FilledButton>
        </div>
      </div>
    </Modal>
  );
}

